import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModalConfig, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from 'services/toast.service';
import { BaseFormModalComponent } from '../base-form-modal.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { ApiService } from 'services/api.service';
import { NgClass } from '@angular/common';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { getControlsWithErrors, MAX_LENGTH_ADDITIONAL_INFO } from 'util/form.utility';
import {
  BackofficeReadingRoomSubmissionCard,
  CreateReadingRoomSubmissionLogAdditionalInfoDto,
  ReadingRoomSubmissionCommunicationDto,
  ReadingRoomSubmissionCommunicationListItemDto,
  UpdateLogAdditionalInformationForReadingRoomSubmissionRequest
} from 'types/service.interface';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { BackofficeStatus } from 'types/visit-room.interface';
import { FILE_OBJECT_TYPE_COMMUNICATION_BACKOFFICE } from 'types/file.interface';
@Component({
  selector: 'app-add-app-client-note-modal',
  standalone: true,
  imports: [TranslateModule, SpinnerComponent, NgClass, TooltipComponent, ReactiveFormsModule, FileUploadComponent, SpinnerComponent],
  templateUrl: './add-app-client-note-modal.component.html',
  styleUrls: ['./add-app-client-note-modal.component.scss', '../../form/base-form.scss']
})
export class AddAppClientNoteModalComponent extends BaseFormModalComponent implements AfterViewInit, OnChanges {
  public readonly maxFieldLength = MAX_LENGTH_ADDITIONAL_INFO;
  @Input() isOpen!: boolean;
  @Input() application!: BackofficeReadingRoomSubmissionCard;
  @Output() update: EventEmitter<ReadingRoomSubmissionCommunicationDto | ReadingRoomSubmissionCommunicationListItemDto> = new EventEmitter<
    ReadingRoomSubmissionCommunicationDto | ReadingRoomSubmissionCommunicationListItemDto
  >();
  @ViewChild('content') content!: TemplateRef<any>;
  public readonly communicationFileType = FILE_OBJECT_TYPE_COMMUNICATION_BACKOFFICE;
  @Input() note: ReadingRoomSubmissionCommunicationDto | ReadingRoomSubmissionCommunicationListItemDto | null = null;
  shouldRenderTooltip = false;

  isCrudLoading = false;
  get backofficeText(): AbstractControl<any, any> | null {
    return this.form.get('backofficeText');
  }
  isSubmittingFiles = false;
  override fileUploadComponentCount = 1;

  constructor(
    formBuilder: FormBuilder,
    config: NgbModalConfig,
    modalService: NgbModal,
    toastService: ToastService,
    translateService: TranslateService,
    private apiService: ApiService
  ) {
    super(formBuilder, config, modalService, toastService, translateService);
  }

  ngAfterViewInit(): void {
    if (this.isOpen) {
      this.fillForm();
      this.open(this.content);
    }
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.fillForm();
      this.shouldRenderTooltip = false;
      this.open(this.content);

      setTimeout(() => {
        this.shouldRenderTooltip = true;
      }, 10);
    }
  }

  fillForm(): void {
    this.form = this.formBuilder.group({
      backofficeText: [this.note?.backofficeText || '', [Validators.required, Validators.maxLength(MAX_LENGTH_ADDITIONAL_INFO)]],
      needToProcess: [false]
    });
  }

  protected override close(): void {
    super.close();
  }

  submit(): void {
    if (this.form.valid) {
      this.isCrudLoading = true;
      if (!this.note) {
        const args: CreateReadingRoomSubmissionLogAdditionalInfoDto = {
          ...this.form.value,
          readingRoomSubmissionId: this.application.id
        };

        this.apiService.createAppClientNote(args).subscribe((response: ReadingRoomSubmissionCommunicationDto) => {
          this.toastService.showSuccess('createAppClientNoteSuccess', '', {
            shouldTranslate: true
          });
          this.close();
          this.note = response;
          this.isSubmittingFiles = true;
        });
      } else {
        const args: UpdateLogAdditionalInformationForReadingRoomSubmissionRequest = {
          ...this.form.value,
          readingRoomSubmissionCommunicationId: this.note.id
        };

        this.apiService
          .updateLogAdditionalInformationForReadingRoomSubmission(args)
          .subscribe((response: ReadingRoomSubmissionCommunicationDto) => {
            this.toastService.showSuccess('updateAppClientNoteSuccess', '', {
              shouldTranslate: true
            });
            this.close();
            this.note = response;
            this.isSubmittingFiles = true;
          });
      }
    } else {
      this.submitAttempted = true;
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'components.addAppClientNote');
    }
  }

  protected override onSubmitSuccess(): void {
    this.isSubmittingFiles = false;

    if (this.note) {
      this.update.emit(this.note);
    }
  }

  get shouldRenderNeedToChangeStatusButton() {
    return this.application?.backofficeStatus === BackofficeStatus.RequestedAdditionalInformation;
  }

  getFiles(note: ReadingRoomSubmissionCommunicationDto | ReadingRoomSubmissionCommunicationListItemDto | null) {
    if (note && 'backofficeFiles' in note) {
      return note.backofficeFiles;
    }

    return [];
  }
}
